import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { APP_URLS, getProjectFormRecordsUrl } from '../../helpers/url_helper';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const THEME_GREEN = '#009391';
const PAGE_SIZES = [5, 10, 25, 50];

const ProjectFormRecords = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();

  const [project, setProject] = useState(() => location.state?.project || null);
  const [submittedForms, setSubmittedForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittedFormsPage, setSubmittedFormsPage] = useState(0);
  const [submittedFormsPageSize, setSubmittedFormsPageSize] = useState(10);

  useEffect(() => {
    const fetchProjectFormRecords = async () => {
      const targetProjectId = String(projectId || project?.id || '').trim();
      if (!targetProjectId) {
        setSubmittedForms([]);
        setError('Project id is missing.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');
        const response = await dataAPI.getDigitalFormSubmissionsByDestination(
          {
            form_destination: 'project',
            project_id: targetProjectId,
          },
          { showLoading: false },
        );
        const apiRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
        setSubmittedForms(apiRows);

        if (!project?.title && !project?.name) {
          const projectsResponse = await dataAPI.getProjectsList({ showLoading: false });
          const projects = Array.isArray(projectsResponse?.data) ? projectsResponse.data : [];
          const matched = projects.find(
            (item) => String(item?.id ?? '') === targetProjectId,
          );
          if (matched) {
            setProject(matched);
          }
        }
      } catch (fetchError) {
        console.error('Error fetching project form submissions:', fetchError);
        setSubmittedForms([]);
        setError(fetchError?.message || 'Unable to load form submissions right now.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectFormRecords();
  }, [projectId, project?.id, project?.title, project?.name]);

  useEffect(() => {
    setSubmittedFormsPage(0);
  }, [submittedForms.length, submittedFormsPageSize]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    const time = parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return `${day}-${month}-${year} ${time}`;
  };

  const paginatedSubmittedForms = submittedForms.slice(
    submittedFormsPage * submittedFormsPageSize,
    submittedFormsPage * submittedFormsPageSize + submittedFormsPageSize,
  );

  const projectTitle =
    project?.title || project?.name || project?.project_name || `Project ${projectId || ''}`;

  const cardStyle = {
    backgroundColor: 'transparent',
    padding: '0',
  };

  return (
    <div
      style={{
        padding: '24px',
        minHeight: '100vh',
        background: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        color: mode === 'dark' ? '#fff' : '#111827',
        fontFamily: 'Urbanist, Arial, sans-serif',
      }}
    >
      <button
        type="button"
        onClick={() => navigate(APP_URLS.PROJECTS)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          border: `1px solid ${mode === 'dark' ? '#333' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '8px 14px',
          background: 'transparent',
          color: mode === 'dark' ? '#fff' : '#111827',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        <ArrowBackIcon style={{ fontSize: 18 }} />
        Back to Projects
      </button>

      <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
        <section style={cardStyle}>
          <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 700 }}>
            Form Records
          </h2>
          <p
            style={{
              margin: '0 0 20px',
              fontSize: '14px',
              color: mode === 'dark' ? '#9ca3af' : '#6b7280',
            }}
          >
            Submitted forms for {projectTitle}
          </p>

          {isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '32px',
              }}
            >
              <img
                src={AppIconBlue}
                alt="Loading"
                className="pulse-animation-icon"
                style={{ width: 40, height: 40 }}
              />
            </div>
          ) : error ? (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: mode === 'dark' ? '#fecaca' : '#b91c1c',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          ) : submittedForms.length === 0 ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: mode === 'dark' ? '#9ca3af' : '#6b7280',
              }}
            >
              No form submissions found.
            </div>
          ) : (
            <Paper
              sx={{
                border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: 'none',
                backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
              }}
            >
              <TableContainer sx={{ maxHeight: 360, overflow: 'auto' }}>
                <Table stickyHeader size="small" aria-label="Project form submissions table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thStyle(mode)}>Form Title</TableCell>
                      <TableCell sx={thStyle(mode)}>Description</TableCell>
                      <TableCell sx={thStyle(mode)}>Submitted By</TableCell>
                      <TableCell sx={thStyle(mode)}>Submitted At</TableCell>
                      <TableCell sx={{ ...thStyle(mode), textAlign: 'center' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedSubmittedForms.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '& td': {
                            borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                          },
                        }}
                      >
                        <TableCell sx={{ ...tdStyle, fontWeight: 600 }}>
                          {row.form_title || `Form ${row.client_form_id || row.id}`}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tdStyle,
                            color: mode === 'dark' ? '#9ca3af' : '#64748b',
                          }}
                        >
                          {row.form_description || '-'}
                        </TableCell>
                        <TableCell sx={tdStyle}>
                          {row.submitted_by_full_name || '-'}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tdStyle,
                            color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                            fontSize: '12px',
                          }}
                        >
                          {formatDateTime(row.submitted_at)}
                        </TableCell>
                        <TableCell sx={{ ...tdStyle, textAlign: 'center' }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() =>
                              navigate(APP_URLS.COMPANY_FORM_RECORD_VIEW, {
                                state: {
                                  form: {
                                    id: row.client_form_id,
                                    title: row.form_title || 'Form Record',
                                    description: row.form_description || '',
                                  },
                                  submission: {
                                    ...row,
                                    answers: row.answers,
                                  },
                                  source: 'project-details',
                                  returnTo: getProjectFormRecordsUrl(projectId),
                                  project,
                                },
                              })
                            }
                            sx={{
                              backgroundColor: THEME_GREEN,
                              borderRadius: '8px',
                              textTransform: 'none',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              fontSize: '12px',
                              '&:hover': { backgroundColor: '#007b79' },
                            }}
                          >
                            View form
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={submittedForms.length}
                page={submittedFormsPage}
                onPageChange={(_, newPage) => setSubmittedFormsPage(newPage)}
                rowsPerPage={submittedFormsPageSize}
                onRowsPerPageChange={(event) => {
                  setSubmittedFormsPageSize(parseInt(event.target.value, 10));
                  setSubmittedFormsPage(0);
                }}
                rowsPerPageOptions={PAGE_SIZES}
                sx={{
                  color: mode === 'dark' ? '#d0d5dd' : '#344054',
                  borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                  backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    margin: 0,
                  },
                }}
              />
            </Paper>
          )}
        </section>
      </div>
    </div>
  );
};

const thStyle = (mode) => ({
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
  fontWeight: 700,
  backgroundColor: mode === 'dark' ? '#111111' : '#f9fafb',
  color: mode === 'dark' ? '#ffffff' : '#111827',
});

const tdStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
};

export default ProjectFormRecords;
